// React
import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';

import './config';

// React components
import HeroMap from './components/map/map';
import CruiseCalls from './components/cruiseCalls';
import LinerServiceAdmin from './components/linerServiceAdmin';
import ArrivalsDepartures from './components/arrivalsDepartures';
import WeatherBlock from './components/weather/weatherBlock';
import ArrivalsDeparturesTeaser from './components/arrivalsDepartures/arrivalsDeparturesTeaser';
import Articles from './components/articles/articles';
import CampaignLinks from './components/campaignLinks/campaignLinks';
import CampaignTestimonials from './components/campaignTestimonials/campaignTestimonials';
import PressreleaseArchive from './components/pressreleaseArchive/PressreleaseArchive';

let mapRoot: Root;
let cruiseCallsRoot: Root;

export interface IReactComponent {
  id: string | null;
  root: Root | null;
  rootElement: HTMLElement | null;
  reactComponent?: React.ReactChild | Iterable<React.ReactNode>;
}

// ------------------------------------------------------------------------------
//  reactComponentIds - a list of react container element ids
// ------------------------------------------------------------------------------

export const reactComponentIds = {
  MAP: 'mapContainer',
  CRUSE_CALLS: 'cruiseCalls',
  LINER_SERVICE_ADMIN: 'linerServiceAdmin',
  ARRIVALS_DEPARTURES: 'arrivalsDepartures',
  WEATHER: 'weatherBlock',
  ARRIVALS_DEPARTURES_TEASER: 'arrivalsDeparturesTeaser',
  ARTICLES_WRAPPER: 'articlesWrapper',
  CAMPAIGN_LINKS_WRAPPER: 'campaignLinksWrapper',
  CAMPAIGN_TESTIMONIALS_SLIDER : 'campaignTestimonialsSlider',
  PRESSRELEASES_WRAPPER: 'pressreleasesWrapper'
};

// ------------------------------------------------------------------------------
//  reactComponents - a list of react components
// ------------------------------------------------------------------------------

export const reactComponents: IReactComponent[] = [
  {
    id: reactComponentIds.MAP,
    root: null,
    rootElement: null,
    reactComponent: <HeroMap show={true} />,
  },
  {
    id: reactComponentIds.CRUSE_CALLS,
    root: null,
    rootElement: null,
    reactComponent: <CruiseCalls show={true} />,
  },
  {
    id: reactComponentIds.LINER_SERVICE_ADMIN,
    root: null,
    rootElement: null,
    reactComponent: <LinerServiceAdmin />,
  },
  {
    id: reactComponentIds.ARRIVALS_DEPARTURES,
    root: null,
    rootElement: null,
    reactComponent: <ArrivalsDepartures show={true} />,
  },
  {
    id: reactComponentIds.WEATHER,
    root: null,
    rootElement: null,
    reactComponent: <WeatherBlock show={true} />,
  },
  {
    id: reactComponentIds.ARRIVALS_DEPARTURES_TEASER,
    root: null,
    rootElement: null,
    reactComponent: <ArrivalsDeparturesTeaser show={true} />,
  },
  {
    id: reactComponentIds.ARTICLES_WRAPPER,
    root: null,
    rootElement: null,
    reactComponent: <Articles />,
  },
  {
    id: reactComponentIds.CAMPAIGN_LINKS_WRAPPER,
    root: null,
    rootElement: null,
    reactComponent: <CampaignLinks/>,
  },
  {
    id: reactComponentIds.CAMPAIGN_TESTIMONIALS_SLIDER ,
    root: null,
    rootElement: null,
    reactComponent: <CampaignTestimonials/>
  },
  {
    id: reactComponentIds.PRESSRELEASES_WRAPPER,
    root: null,
    rootElement: null,
    reactComponent: <PressreleaseArchive />,
  }
];

// -----------------------------------------------------------------v--------------
//  setReactComponent - creates a react root container and render the component
// --------------------------------------------------------------------------------

export const setReactComponent = (id: string) => {
  let componentData: IReactComponent;
  if (id) {
    const component = reactComponents.find((component) => component.id === id);
    if (component) {
      const rootElement = document.getElementById(id);
      if (rootElement && !component.root) {
        componentData = component;
        componentData.root = createRoot(rootElement!);
        componentData.rootElement = rootElement;
        renderReactComponent(componentData);
      } else {
        console.log(`setReactComponent: No element with id ${id} found`);
      }
    } else {
      console.log(`setReactComponent: React component with ${id} not found`);
    }
  } else {
    console.log(`setReactComponent: you have to provide an component id`);
  }
};

// -------------------------------------------------------------------------------------
//  updateReactComponent - updates react component if it exists otherwise it creates it
// -------------------------------------------------------------------------------------

export const updateReactComponent = (id: string) => {
  if (id) {
    const component = reactComponents.find((component) => component.id === id);
    if (component?.root) {
      renderReactComponent(component);
    } else {
      setReactComponent(id);
    }
  } else {
    console.log(`UpdateReactComponent: React component with ${id} not found`);
  }
};

// -------------------------------------------------------------------------------------
//  renderReactComponent - renders the react component
// -------------------------------------------------------------------------------------

const renderReactComponent = ({ root, reactComponent }: IReactComponent) => {
  if (reactComponent) {
    root?.render(reactComponent);
  }
};
